// 2D outlines for cut-outs (part.cutouts): windows that are cut right through a part.
// Coordinates (a, b) are in the face's plane, from the part's centre — the same as holes.

const TAU = Math.PI * 2

function arc(c, r, a0, a1, steps) {
  const out = []
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps
    out.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r])
  }
  return out
}
/** Shortest sweep from angle a0 to a1. */
const sweep = (a0, a1) => { let d = a1 - a0; while (d > Math.PI) d -= TAU; while (d < -Math.PI) d += TAU; return a0 + d }
const ang = (p, c = [0, 0]) => Math.atan2(p[1] - c[1], p[0] - c[0])

/**
 * Windows between the spokes of a wheel: everything between the hub (radius rIn) and the
 * rim (radius rOut) except `spokes` straight spokes of `width`, every corner rounded with
 * radius `fillet`. Returns one closed polygon per window (counter-clockwise).
 */
export function spokeWindows({ rIn, rOut, spokes = 4, width = 30, fillet = 12, rotate = 0, seg = 12 }) {
  const d = width / 2, f = fillet
  if (rOut - rIn < 2 * f + 4) return []
  const out = []
  for (let k = 0; k < spokes; k++) {
    const p1 = rotate + (k * TAU) / spokes, p2 = rotate + ((k + 1) * TAU) / spokes
    const n1 = [-Math.sin(p1), Math.cos(p1)], n2 = [Math.sin(p2), -Math.cos(p2)]
    // Fillet circle tangent to a spoke edge and a circle (outer: inside it, inner: outside it).
    const corner = (phi, n, side, R, outer) => {
      const rho = outer ? R - f : R + f
      if ((d + f) / rho >= 1) return null
      const th = phi + side * Math.asin((d + f) / rho)
      const c = [Math.cos(th) * rho, Math.sin(th) * rho]
      return { c, onCircle: [c[0] * (R / rho), c[1] * (R / rho)], onLine: [c[0] - n[0] * f, c[1] - n[1] * f] }
    }
    const o1 = corner(p1, n1, 1, rOut, true), o2 = corner(p2, n2, -1, rOut, true)
    const i1 = corner(p1, n1, 1, rIn, false), i2 = corner(p2, n2, -1, rIn, false)
    if (!o1 || !o2 || !i1 || !i2) continue
    const fil = (k0, from, to) => { const a0 = ang(from, k0.c); return arc(k0.c, f, a0, sweep(a0, ang(to, k0.c)), Math.max(4, seg / 2)) }
    const poly = [
      ...fil(o1, o1.onLine, o1.onCircle),
      ...(() => { const a0 = ang(o1.onCircle), a1 = ang(o2.onCircle); return arc([0, 0], rOut, a0, a0 + ((a1 - a0 + TAU) % TAU), seg * 3).slice(1, -1) })(),
      ...fil(o2, o2.onCircle, o2.onLine),
      ...fil(i2, i2.onLine, i2.onCircle),
      ...(() => { const a0 = ang(i2.onCircle), a1 = ang(i1.onCircle); return arc([0, 0], rIn, a0, a0 - ((a0 - a1 + TAU) % TAU), seg).slice(1, -1) })(),
      ...fil(i1, i1.onCircle, i1.onLine),
    ]
    out.push(poly.map(([a, b]) => [Math.round(a * 100) / 100, Math.round(b * 100) / 100]))
  }
  return out
}

/** Signed area (positive = counter-clockwise) and bounding box of a polygon. */
export function polyInfo(poly) {
  let s = 0
  for (let i = 0; i < poly.length; i++) { const p = poly[i], q = poly[(i + 1) % poly.length]; s += p[0] * q[1] - q[0] * p[1] }
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1])
  return { area: s / 2, w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), cx: (Math.max(...xs) + Math.min(...xs)) / 2, cy: (Math.max(...ys) + Math.min(...ys)) / 2 }
}

/**
 * Half of a ring: outer arc from angle a0 over 180°, back along the inner arc. With ri = 0
 * it is a half disc (outer arc and the diameter line). Counter-clockwise, centre at 0, 0.
 */
export function halfRing({ ro, ri = 0, a0 = 0, seg = 96 }) {
  const out = []
  for (let i = 0; i <= seg; i++) { const a = a0 + (Math.PI * i) / seg; out.push([Math.cos(a) * ro, Math.sin(a) * ro]) }
  if (ri > 0) for (let i = seg; i >= 0; i--) { const a = a0 + (Math.PI * i) / seg; out.push([Math.cos(a) * ri, Math.sin(a) * ri]) }
  return out.map(([a, b]) => [Math.round(a * 100) / 100, Math.round(b * 100) / 100])
}

/** Rectangle with rounded corners (radius r), counter-clockwise. */
export function roundedRect(x0, y0, x1, y1, r = 0, seg = 6) {
  r = Math.max(0, Math.min(r, (x1 - x0) / 2, (y1 - y0) / 2))
  if (!r) return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]
  const out = []
  const corner = (cx, cy, a0) => { for (let i = 0; i <= seg; i++) { const a = a0 + (Math.PI / 2) * (i / seg); out.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]) } }
  corner(x1 - r, y0 + r, -Math.PI / 2)
  corner(x1 - r, y1 - r, 0)
  corner(x0 + r, y1 - r, Math.PI / 2)
  corner(x0 + r, y0 + r, Math.PI)
  return out.map(([a, b]) => [Math.round(a * 100) / 100, Math.round(b * 100) / 100])
}

/**
 * Shoji / kumiko grid: the windows of a lattice `w` × `h` (centred) with a border `rand`,
 * `cols` × `rows` fields and bars `steg` wide; window corners rounded by `r`.
 */
export function shojiWindows({ w, h, rand, steg, cols, rows, r = 1.5, randBottom = rand }) {
  // randBottom: a wider solid panel at the bottom (koshi-ita), as on shoji doors.
  const iw = w - 2 * rand, ih = h - rand - randBottom
  const cw = (iw - (cols - 1) * steg) / cols, ch = (ih - (rows - 1) * steg) / rows
  if (cw < 4 || ch < 4) return []
  const out = []
  for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++) {
    const x0 = -w / 2 + rand + i * (cw + steg), y0 = -h / 2 + randBottom + j * (ch + steg)
    out.push(roundedRect(x0, y0, x0 + cw, y0 + ch, r))
  }
  return out
}

/**
 * Lattice with rows of different heights: `rowH` lists the rows from the bottom, in mm;
 * a 0 takes an equal share of what is left. Vertical bars run through every row, as on an
 * andon front (tall slats above, short fields below).
 */
export function latticeWindows({ w, h, rand, randBottom = rand, steg, cols, rowH = [0], r = 1.5 }) {
  const iw = w - 2 * rand, ih = h - rand - randBottom
  const cw = (iw - (cols - 1) * steg) / cols
  const fixed = rowH.reduce((s, x) => s + x, 0), free = rowH.filter((x) => !x).length
  const rest = (ih - (rowH.length - 1) * steg - fixed) / Math.max(1, free)
  const hs = rowH.map((x) => x || rest)
  if (cw < 4 || hs.some((x) => x < 4)) return []
  const out = []
  let y0 = -h / 2 + randBottom
  for (const ch of hs) {
    for (let i = 0; i < cols; i++) {
      const x0 = -w / 2 + rand + i * (cw + steg)
      out.push(roundedRect(x0, y0, x0 + cw, y0 + ch, r))
    }
    y0 += ch + steg
  }
  return out
}
